import React, { useEffect, useState, useRef, useCallback } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import axios from "axios";
import { useCart } from "../contexts/CartContext";
import { useAuth } from "../contexts/AuthContext";
import "./product-details.css";

const PRODUCT_API = process.env.REACT_APP_PRODUCT_SERVICE_URL;
const REVIEWS_API = process.env.REACT_APP_PRODUCT_SERVICE_URL;
const INVENTORY_API_BASE = process.env.REACT_APP_INVENTORY_SERVICE_URL;

export default function ProductDetail() {
  const { slug } = useParams();

  // extract ID from slug
  const productId = slug.split("-").pop();

  const navigate = useNavigate();
  const { auth, isAuthenticated } = useAuth();
  const { cart, addToCart, isInCart } = useCart();

  const [product, setProduct] = useState(null);
  const [related, setRelated] = useState([]);
  const [quantity, setQuantity] = useState(1);
  const [selectedImage, setSelectedImage] = useState("");
  const [reviews, setReviews] = useState([]);
  const [newReview, setNewReview] = useState({
    rating: 5,
    comment: "",
    images: [],
  });
  const [submitting, setSubmitting] = useState(false);
  const [copied, setCopied] = useState(false);
  const [zoomLevel, setZoomLevel] = useState(2);
  const imgRef = useRef(null);
  const zoomRef = useRef(null);
  const [lensVisible, setLensVisible] = useState(false);
  const [lensPos, setLensPos] = useState({ x: 0, y: 0 });
  const [bgPos, setBgPos] = useState("0% 0%");

  // const getImageUrl = (imgMeta) =>
  //   imgMeta
  //     ? `https://auth.afrogebeya.com/uploads/products/${imgMeta.storedName.replace(
  //         /\\/g,
  //         "/",
  //       )}`
  //     : "https://via.placeholder.com/400";

  const getImageUrl = (imgMeta) =>
    imgMeta
      ? `https://auth.afrogebeya.com/uploads/products/${imgMeta.replace(
          /\\/g,
          "/",
        )}`
      : "https://via.placeholder.com/400";
  /* ---------------- FETCH PRODUCT ---------------- */
  useEffect(() => {
    let mounted = true;
    const fetchProduct = async () => {
      try {
        const res = await axios.get(`${PRODUCT_API}/products/${productId}`);
        if (!mounted) return;
        const prod = res.data;

        setProduct(prod);
        setSelectedImage(getImageUrl(prod.mainImageUrl || prod.imageUrls?.[0]));

        // related products
        if (prod.categoryId) {
          const relatedRes = await axios.get(`${PRODUCT_API}/products`, {
            params: { categoryId: prod.categoryId, page: 0, size: 8 },
          });
          if (!mounted) return;
          setRelated(
            relatedRes.data.content.filter(
              (p) => p.productId !== prod.productId,
            ),
          );
        }
      } catch (err) {
        console.error("Failed to fetch product.");
      }
    };
    fetchProduct();
    return () => (mounted = false);
  }, [productId]);

  useEffect(() => {
    // Scroll to top whenever productId changes
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, [productId]);

  /* ---------------- FETCH REVIEWS ---------------- */
  const fetchReviews = useCallback(async () => {
    try {
      const res = await axios.get(`${REVIEWS_API}/products/${productId}`);
      const data = Array.isArray(res.data) ? res.data : [];
      setReviews(data);
    } catch (err) {
      console.error("Failed to fetch reviews.");
    }
  }, [productId]);

  /* ---------------- CART LOGIC ---------------- */

  const getDefaultVariant = (product) => {
    return (
      product?.variants?.find((v) => v.isDefault) ||
      product?.variants?.[0] ||
      null
    );
  };
  const selectedVariant = getDefaultVariant(product);

  const cartItem = cart.find((item) => item.variantId === selectedVariant?.id);

  const maxReached =
    cartItem && selectedVariant?.availableQuantity != null
      ? cartItem.quantity >= selectedVariant.availableQuantity
      : false;

  const handleQuantityChange = (e) => {
    let val = parseInt(e.target.value, 10);
    if (isNaN(val) || val < 1) val = 1;

    const remaining =
      (selectedVariant?.availableQuantity || 1) - (cartItem?.quantity || 0);
  };

  const handleAddToCart = () => {
    if (!product || !selectedVariant) return;

    addToCart(product, quantity);
    setQuantity(1);
  };

  useEffect(() => {
    const img = imgRef.current;
    if (!img) return;

    const handleWheel = (e) => {
      if (!e.ctrlKey) return;
      e.preventDefault();

      setZoomLevel((prev) => {
        let next = prev + (e.deltaY < 0 ? 0.2 : -0.2);
        return Math.min(Math.max(next, 1.5), 4);
      });
    };

    img.addEventListener("wheel", handleWheel, { passive: false });

    return () => {
      img.removeEventListener("wheel", handleWheel);
    };
  }, []);

  /* ---------------- ZOOM & LENS ---------------- */
  const handleMouseMove = (e) => {
    const rect = imgRef.current.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;
    const xPercent = (x / rect.width) * 100;
    const yPercent = (y / rect.height) * 100;
    setLensPos({ x, y });
    setBgPos(`${xPercent}% ${yPercent}%`);
    if (zoomRef.current) {
      zoomRef.current.style.backgroundSize = `${zoomLevel * 100}%`;
      zoomRef.current.style.backgroundPosition = `${xPercent}% ${yPercent}%`;
    }
  };

  const handleTouchMove = (e) => {
    if (e.touches.length === 2) {
      setZoomLevel((prev) => Math.min(prev + 0.05, 4));
    }
  };

  const handleWheel = (e) => {
    e.preventDefault();

    setZoomLevel((prev) => {
      let next = prev + (e.deltaY < 0 ? 0.2 : -0.2);
      return Math.min(Math.max(next, 1.5), 4);
    });
  };

  /* ---------------- REVIEW SUBMISSION ---------------- */
  const handleReviewSubmit = async (e) => {
    e.preventDefault();
    if (!isAuthenticated) return alert("Login required.");
    setSubmitting(true);
    try {
      const formData = new FormData();
      formData.append("productId", productId);
      formData.append("rating", newReview.rating);
      formData.append("comment", newReview.comment);
      newReview.images.forEach((file) => formData.append("images", file));

      await axios.post(`${REVIEWS_API}`, formData, {
        headers: {
          Authorization: `Bearer ${auth.token}`,
          "Content-Type": "multipart/form-data",
        },
      });
      setNewReview({ rating: 5, comment: "", images: [] });
      fetchReviews();
    } catch (err) {
      console.error(err);
      alert("Failed to submit review.");
    } finally {
      setSubmitting(false);
    }
  };

  /* ---------------- RATING SUMMARY ---------------- */
  const ratingCounts = [5, 4, 3, 2, 1].map(
    (star) => reviews.filter((r) => r.rating === star).length,
  );
  const averageRating =
    reviews.length > 0
      ? reviews.reduce((sum, r) => sum + r.rating, 0) / reviews.length
      : 0;

  if (!product)
    return (
      <div className="placeholder-glow">
        <span className="placeholder col-12"></span>
      </div>
    );

  const formattedPrice = new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: product.currency || "USD",
  }).format(product.price || 0);
  const isSeller = auth?.roles?.includes("SELLER");

  const shareUrl = `https://afrogebeya.com/products/preview/${slug}`;
  const encodedUrl = encodeURIComponent(shareUrl);
  const encodedText = encodeURIComponent(product?.productName || "");

  const whatsappShare = `https://wa.me/?text=${encodedText}%20${encodedUrl}`;
  const telegramShare = `https://t.me/share/url?url=${encodedUrl}&text=${encodedText}`;
  const facebookShare = `https://www.facebook.com/sharer/sharer.php?u=${encodedUrl}`;

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopied(true);

      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error("Copy failed", err);
    }
  };

  const handleShare = async () => {
    if (!product) return;

    try {
      if (navigator.share) {
        await navigator.share({
          title: product.productName,
          text: product.description?.slice(0, 100),
          url: shareUrl,
        });
      } else {
        await navigator.clipboard.writeText(shareUrl);
        alert("Product link copied!");
      }
    } catch (err) {
      console.error("Share failed:", err);
    }
  };

  return (
    <>
      <div className="container py-4">
        <nav aria-label="breadcrumb">
          <ol className="breadcrumb mb-3 small">
            <li className="breadcrumb-item">
              <Link to="/" className="text-decoration-none">
                <i className="bi bi-house-door me-1"></i>
                Home
              </Link>
            </li>

            <li className="breadcrumb-item">
              <Link
                to={`/category/${product.categoryId}`}
                className="text-decoration-none"
              >
                {product.categoryName}
              </Link>
            </li>

            <li className="breadcrumb-item active fw-semibold text-dark">
              {product.productName}
            </li>
          </ol>
        </nav>

        <div
          className="row g-4 position-relative"
          style={{ overflow: "visible" }}
        >
          <div className="col-lg-6 d-flex flex-column flex-md-row gap-3">
            {/* MAIN IMAGE */}
            <div
              className="position-relative flex-grow-1 text-center order-1 order-md-2"
              style={{ overflow: "visible", zIndex: 20 }}
            >
              <img
                loading="lazy"
                ref={imgRef}
                src={selectedImage}
                alt={product.productName}
                onTouchMove={handleTouchMove}
                className="img-fluid rounded border shadow-sm"
                style={{
                  maxHeight: 500,
                  objectFit: "contain",
                  width: "100%",
                  background: "#fff",
                }}
                onMouseEnter={() =>
                  window.innerWidth > 768 && setLensVisible(true)
                }
                onMouseLeave={() => setLensVisible(false)}
                onMouseMove={(e) =>
                  window.innerWidth > 768 && handleMouseMove(e)
                }
              />

              {lensVisible && (
                <>
                  <div
                    className="lens-rect"
                    style={{
                      left: lensPos.x - 75,
                      top: lensPos.y - 50,
                      backgroundImage: `url(${selectedImage})`,
                      backgroundPosition: bgPos,
                    }}
                  />
                  <div
                    ref={zoomRef}
                    className="zoom-box"
                    style={{ backgroundImage: `url(${selectedImage})` }}
                  />
                </>
              )}
            </div>

            {/* THUMBNAILS */}
            <div
              className="d-flex flex-row flex-md-column gap-2 overflow-auto order-2 order-md-1"
              style={{ maxWidth: "100%" }}
            >
              {product.mainImage && (
                <img
                  src={getImageUrl(product.mainImageUrl)}
                  alt="thumb"
                  className={`img-thumbnail ${
                    selectedImage === getImageUrl(product.mainImageUrl)
                      ? "border-primary shadow"
                      : "border"
                  }`}
                  style={{
                    width: 80,
                    height: 80,
                    objectFit: "cover",
                    flexShrink: 0,
                  }}
                  onClick={() =>
                    setSelectedImage(getImageUrl(product.mainImageUrl))
                  }
                />
              )}

              {product.imageUrls?.map((img, idx) => (
                <img
                  key={idx}
                  src={getImageUrl(img)}
                  alt={`thumb-${idx}`}
                  className={`img-thumbnail ${
                    selectedImage === getImageUrl(img) ? "border-primary" : ""
                  }`}
                  style={{
                    width: 80,
                    height: 80,
                    objectFit: "cover",
                    flexShrink: 0,
                  }}
                  onClick={() => setSelectedImage(getImageUrl(img))}
                />
              ))}
            </div>
          </div>

          <div className="col-lg-6" style={{ position: "relative", zIndex: 1 }}>
            <div className="sticky-top" style={{ top: 20 }}>
              {/* <h2 className="fw-bold mb-2">{product.productName}</h2> */}
              <div className="d-flex justify-content-between align-items-start mb-2 flex-wrap gap-2">
                <h2 className="fw-bold mb-0">{product.productName}</h2>

                <div className="d-flex gap-2 flex-wrap">
                  {/* Native Share */}
                  <button
                    className="btn btn-light border"
                    onClick={handleShare}
                    title="Share"
                  >
                    <i className="bi bi-share"></i>
                  </button>

                  {/* WhatsApp */}
                  <a
                    href={whatsappShare}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="btn btn-success"
                    title="Share on WhatsApp"
                  >
                    <i className="bi bi-whatsapp"></i>
                  </a>

                  {/* Telegram */}
                  <a
                    href={telegramShare}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="btn btn-primary"
                    title="Share on Telegram"
                  >
                    <i className="bi bi-telegram"></i>
                  </a>

                  {/* Facebook */}
                  <a
                    href={facebookShare}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="btn btn-primary"
                    title="Share on Facebook"
                  >
                    <i className="bi bi-facebook"></i>
                  </a>

                  {/* Copy Link */}
                  <button
                    className={`btn ${copied ? "btn-success" : "btn-outline-secondary"}`}
                    onClick={handleCopy}
                    title="Copy link"
                  >
                    {copied ? (
                      <>
                        <i className="bi bi-check-lg"></i> Copied
                      </>
                    ) : (
                      <>
                        <i className="bi bi-link-45deg"></i> Copy Link
                      </>
                    )}
                  </button>
                </div>
              </div>
              <p className="text-muted">{product.description}</p>

              {/* Rating Display */}
              <div className="d-flex align-items-center gap-2 mb-2">
                <span className="text-warning fs-5">
                  {"★".repeat(Math.round(averageRating))}
                  {"☆".repeat(5 - Math.round(averageRating))}
                </span>
                <small className="text-muted">({reviews.length} reviews)</small>
              </div>

              {/* Price Block */}
              <div className="mb-3">
                <div className="fs-3 fw-bold ">{formattedPrice}</div>
                {product.discountPrice !== 0 && (
                  <div className="text-muted text-decoration-line-through">
                    {product.price}
                  </div>
                )}
              </div>

              {/* Stock Indicator */}
              <div className="mb-3">
                {selectedVariant?.availableQuantity > 0 ? (
                  <span className="badge bg-success-subtle text-success">
                    In Stock ({selectedVariant.availableQuantity})
                  </span>
                ) : (
                  <span className="badge bg-danger-subtle text-danger">
                    Out of Stock
                  </span>
                )}
              </div>

              {/* Quantity*/}
              <div className="card border-0 shadow-sm p-3 mb-3">
                <div className="mb-2">Quantity</div>
                <input
                  type="number"
                  min="1"
                  max={selectedVariant?.availableQuantity ?? 1}
                  value={quantity}
                  onChange={handleQuantityChange}
                  className="form-control mb-3"
                />

                <button
                  className="btn btn-warning w-100 mb-2 fw-semibold"
                  onClick={handleAddToCart}
                  disabled={!selectedVariant?.availableQuantity}
                >
                  🛒{" "}
                  {maxReached
                    ? "Max in Cart"
                    : isInCart(selectedVariant?.id)
                      ? "Add More"
                      : "Add to Cart"}
                </button>

                <button
                  className="btn btn-dark w-100 fw-semibold"
                  disabled={!selectedVariant?.availableQuantity}
                  onClick={() => navigate("/cart")}
                >
                  Buy Now
                </button>
              </div>

              {/* Trust Badges */}
              <div className="small text-muted">
                ✓ Secure Payment
                <br />
                ✓ 7 Days Return Policy
                <br />✓ Verified Seller
              </div>
            </div>
          </div>

          <hr className="my-5" />

          <div className="row">
            <div className="col-lg-8">
              <h4 className="mb-3">Product Description</h4>
              <p className="text-muted">{product.description}</p>

              <h5 className="mt-4">Specifications</h5>
              <table className="table table-sm">
                <tbody>
                  <tr>
                    <th>Brand</th>
                    <td>{product.brand || "N/A"}</td>
                  </tr>
                  <tr>
                    <th>Model</th>
                    <td>{product.modelNumber || "N/A"}</td>
                  </tr>
                  <tr>
                    <th>SKU</th>
                    <td>{product.sku}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>

        {/* Rating Summary */}
        <div className="mt-5">
          <div className="card border-0 shadow-sm p-4">
            <h4 className="mb-3">Customer Reviews</h4>

            <div className="d-flex align-items-center gap-3 mb-3">
              <div className="display-5 fw-bold">
                {averageRating.toFixed(1)}
              </div>
              <div>
                <div className="text-warning fs-4">
                  {"★".repeat(Math.round(averageRating))}
                  {"☆".repeat(5 - Math.round(averageRating))}
                </div>
                <small className="text-muted">
                  Based on {reviews.length} reviews
                </small>
              </div>
            </div>
          </div>
        </div>

        {/* Reviews Section */}
        <div className="mt-5">
          <h5>Reviews ({reviews.length})</h5>
          {reviews.map((r) => (
            <div key={r.id} className="card border-0 shadow-sm mb-3">
              <div className="card-body">
                <div className="d-flex justify-content-between">
                  <strong>{r.email}</strong>
                  <small className="text-muted">
                    {new Date(r.createdAt).toLocaleDateString()}
                  </small>
                </div>

                <div className="text-warning mb-2">
                  {"★".repeat(r.rating)}
                  {"☆".repeat(5 - r.rating)}
                </div>

                <p className="mb-2">{r.comment}</p>

                {r.images?.length > 0 && (
                  <div className="d-flex gap-2">
                    {r.images.map((img) => (
                      <img
                        key={img.id}
                        src={`https://auth.afrogebeya.com/uploads/products/${img.storedName}`}
                        style={{
                          width: 70,
                          height: 70,
                          objectFit: "cover",
                          borderRadius: 8,
                        }}
                      />
                    ))}
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>
    </>
  );
}
